import i18n from '../i18n';
import { translateText } from './translateText';

// Cache for name translations
const nameCache = new Map();

/**
 * Transliterates a name to the current selected language
 * @param {string} name - The name to transliterate
 * @param {Function} t - Optional translation function (for reactivity)
 * @returns {string} - Transliterated name or original if translation fails
 */
export const translateName = async (name, t = null) => {
  if (!name || typeof name !== 'string' || name.trim() === '') {
    return name;
  }

  const trimmedName = name.trim();
  const currentLang = i18n.language || 'en';

  // If language is English, return as-is
  if (currentLang === 'en') {
    return trimmedName;
  }

  // Check cache first
  const cacheKey = `${trimmedName}_${currentLang}`;
  if (nameCache.has(cacheKey)) {
    return nameCache.get(cacheKey);
  }

  // Check localStorage cache
  try {
    const storedCache = localStorage.getItem('nameTranslationCache');
    if (storedCache) {
      const cache = JSON.parse(storedCache);
      if (cache[cacheKey]) {
        nameCache.set(cacheKey, cache[cacheKey]);
        return cache[cacheKey];
      }
    }
  } catch (e) {
    // Invalid cache, ignore
  }

  // Use translateText to transliterate the name
  try {
    const translated = await translateText(trimmedName, currentLang);
    
    // Cache the translation
    nameCache.set(cacheKey, translated);
    
    // Update localStorage cache (keep last 50 name translations)
    try {
      const storedCache = localStorage.getItem('nameTranslationCache');
      let cache = storedCache ? JSON.parse(storedCache) : {};
      cache[cacheKey] = translated;
      
      // Keep only last 50 entries
      const keys = Object.keys(cache);
      if (keys.length > 50) {
        const oldestKeys = keys.slice(0, keys.length - 50);
        oldestKeys.forEach(key => delete cache[key]);
      }
      
      localStorage.setItem('nameTranslationCache', JSON.stringify(cache));
    } catch (e) {
      // Ignore localStorage errors
    }
    
    return translated;
  } catch (error) {
    console.error('Name translation error:', error);
    // On error, return original name
    nameCache.set(cacheKey, trimmedName);
    return trimmedName;
  }
};

/**
 * Synchronous version that returns cached translation or original name
 * Use this for immediate display, then update with async version
 * @param {string} name - The name to transliterate
 * @returns {string} - Cached transliterated name or original
 */
export const translateNameSync = (name) => {
  if (!name || typeof name !== 'string' || name.trim() === '') {
    return name;
  }

  const trimmedName = name.trim();
  const currentLang = i18n.language || 'en';

  // If language is English, return as-is
  if (currentLang === 'en') {
    return trimmedName;
  }

  // Check cache first
  const cacheKey = `${trimmedName}_${currentLang}`;
  if (nameCache.has(cacheKey)) {
    return nameCache.get(cacheKey);
  }

  // Check localStorage cache
  try {
    const storedCache = localStorage.getItem('nameTranslationCache');
    if (storedCache) {
      const cache = JSON.parse(storedCache);
      if (cache[cacheKey]) {
        nameCache.set(cacheKey, cache[cacheKey]);
        return cache[cacheKey];
      }
    }
  } catch (e) {
    // Invalid cache, ignore
  }

  // Return original if not cached
  return trimmedName;
};

/**
 * Translates multiple names
 * @param {string[]} names - Array of names to transliterate
 * @returns {Promise<string[]>} - Array of transliterated names
 */
export const translateNames = async (names) => {
  if (!Array.isArray(names) || names.length === 0) {
    return names;
  }

  const translated = await Promise.all(
    names.map(name => translateName(name))
  );

  return translated;
};

/**
 * Clears the name translation cache
 */
export const clearNameCache = () => {
  nameCache.clear();
  localStorage.removeItem('nameTranslationCache');
};

export default translateName;

